import type { QuoteCartQuote } from '@/components/quotes/quoteCartItems'
import type { BadgeVariant } from '@/components/ui/badge'
import { COVERAGE_TYPE_LABELS, CoverageType } from '@/models/insuranceCoverage'

const CARRIER_LOGO_BASE = 'https://static.cai.insure/carriers'

// The app's CoverageType has no workers' comp yet, so the prototype carries its own.
const WORKERS_COMP = 'WORKERS_COMP' as CoverageType

export const COVERAGE_LABELS: Record<string, string> = {
  ...COVERAGE_TYPE_LABELS,
  [WORKERS_COMP]: "Workers' Comp",
}

export enum CheckoutRequirement {
  EFFECTIVE_DATE = 'effective_date',
  SIGNATURES = 'signatures',
  PAYMENT_PLAN = 'payment_plan',
  PAYMENT_INFO = 'payment_info',
  COVERAGE_ACTIVATION = 'coverage_activation',
  COI = 'coi',
  DOCUMENT_UPLOAD = 'document_upload',
  TERMS = 'terms',
  COI_HOLDERS = 'coi_holders',
  CARRIER_PAYMENT = 'carrier_payment',
}

export const REQUIREMENT_COPY: Record<
  CheckoutRequirement,
  {
    title: string | ((carrierName: string) => string)
    optional?: boolean
    /** Open to the customer from the start, outside the step-by-step order. */
    anytime?: boolean
    /** Open on a carrier estimate too; every other step waits for the carrier's final pricing. */
    beforeFinalPricing?: boolean
  }
> = {
  [CheckoutRequirement.EFFECTIVE_DATE]: {
    title: 'Confirm start date',
  },
  [CheckoutRequirement.SIGNATURES]: {
    title: 'Sign your forms',
  },
  [CheckoutRequirement.PAYMENT_PLAN]: {
    title: 'Choose a payment plan',
  },
  [CheckoutRequirement.PAYMENT_INFO]: {
    title: 'Add payment details',
  },
  [CheckoutRequirement.COVERAGE_ACTIVATION]: {
    title: 'Coverage activation',
  },
  [CheckoutRequirement.COI]: {
    title: 'COI',
    optional: true,
  },
  [CheckoutRequirement.DOCUMENT_UPLOAD]: {
    title: 'Complete requested info',
    beforeFinalPricing: true,
  },
  [CheckoutRequirement.TERMS]: {
    title: 'Accept terms and confirm info',
  },
  [CheckoutRequirement.COI_HOLDERS]: {
    title: 'Add COI holders',
    optional: true,
    anytime: true,
    beforeFinalPricing: true,
  },
  [CheckoutRequirement.CARRIER_PAYMENT]: {
    title: 'Payment',
  },
}

export enum StepStatus {
  MISSING_INFO = 'missing_info',
  COMPLETE = 'complete',
  PENDING = 'pending',
  SUCCESS = 'success',
  FAILURE = 'failure',
  IN_PROGRESS = 'in_progress',
  SENT = 'sent',
  ACTIVE = 'active',
  CANCELLED = 'cancelled',
}

export const STATUS_BADGES: Record<StepStatus, { label: string; variant: BadgeVariant }> = {
  [StepStatus.MISSING_INFO]: { label: 'Missing info', variant: 'warning' },
  [StepStatus.COMPLETE]: { label: 'Complete', variant: 'success' },
  [StepStatus.PENDING]: { label: 'Pending', variant: 'secondary' },
  [StepStatus.SUCCESS]: { label: 'Payment complete', variant: 'success' },
  [StepStatus.FAILURE]: { label: 'Failure', variant: 'destructive' },
  [StepStatus.IN_PROGRESS]: { label: 'In progress', variant: 'secondary' },
  [StepStatus.SENT]: { label: 'Sent', variant: 'success' },
  [StepStatus.ACTIVE]: { label: 'Active', variant: 'success' },
  [StepStatus.CANCELLED]: { label: 'Canceled', variant: 'destructive' },
}

export interface RequiredDocument {
  name: string
  /** Already on file, so there's nothing to upload. */
  isComplete?: boolean
  /** A service is fetching it; the customer can upload it themselves instead. */
  awaitingFrom?: string
}

export interface PaymentPlan {
  title: string
  dueToday: number
  /** The payments after today's; null when paid in full. */
  installments?: { count: number; amount: number } | null
  /** What the policy costs on this plan, installment fees included. */
  total: number
}

export interface CarrierCheckout {
  quote: QuoteCartQuote
  isCarrierEstimate: boolean
  paymentPlans: PaymentPlan[]
  /** Takes ACH as well as card. */
  acceptsAch?: boolean
  /** Can reuse a card already given to another provider in this checkout. */
  offerSameCard?: boolean
  /** Documents the provider needs uploaded before it will bind. */
  requiredDocuments?: RequiredDocument[]
  /** Mock: coverage goes active this many seconds after payment, in place of waiting for the start date. */
  activatesInSeconds?: number
  /** Shows what happens after payment, step by step, through coverage going active. */
  tracksActivation?: boolean
  /** In this provider's own order: no two providers run checkout the same way. */
  requirements: CheckoutRequirement[]
}

export const CHECKOUTS: CarrierCheckout[] = [
  {
    quote: {
      id: 'corgi',
      carrierName: 'Corgi',
      iconUrl: `${CARRIER_LOGO_BASE}/corgi-icon.png`,
      iconAlt: 'Corgi',
      coverages: [
        { type: CoverageType.AUTO_LIABILITY, premium: 14_200, limit: 1_000_000, deductible: null },
        { type: CoverageType.MOTOR_TRUCK_CARGO, premium: 1_850, limit: 100_000, deductible: 1_000 },
      ],
      oneTimeFees: [
        { id: 'corgi-policy-fee', label: 'Policy fee', amount: 250 },
        { id: 'corgi-surplus-lines-tax', label: 'Surplus lines tax', amount: 482 },
      ],
      keys: ['corgi:AUTO_LIABILITY', 'corgi:MOTOR_TRUCK_CARGO'],
      subtotal: 16_782,
      pricedAsOneTotal: false,
    },
    isCarrierEstimate: false,
    paymentPlans: [
      { title: 'Pay in full', dueToday: 16_782, total: 16_782 },
      { title: '4 payments', dueToday: 5227, installments: { count: 3, amount: 3935 }, total: 17_032 },
      { title: '9 payments', dueToday: 3917, installments: { count: 8, amount: 1640 }, total: 17_037 },
      { title: '11 payments', dueToday: 3752, installments: { count: 10, amount: 1335 }, total: 17_102 },
    ],
    requiredDocuments: [
      { name: 'Loss run reports', isComplete: true },
      { name: 'IFTA return', isComplete: true },
      { name: 'MVRs', awaitingFrom: 'Checkr' },
    ],
    tracksActivation: true,
    activatesInSeconds: 6,
    requirements: [
      CheckoutRequirement.DOCUMENT_UPLOAD,
      CheckoutRequirement.COI_HOLDERS,
      CheckoutRequirement.TERMS,
      CheckoutRequirement.CARRIER_PAYMENT,
    ],
  },
  {
    quote: {
      id: 'progressive',
      carrierName: 'Progressive',
      iconUrl: `${CARRIER_LOGO_BASE}/progressive-icon.png`,
      iconAlt: 'Progressive',
      coverages: [
        { type: CoverageType.AUTO_PHYSICAL_DAMAGE, premium: 4_600, limit: 240_000, deductible: 2_500 },
      ],
      oneTimeFees: [{ id: 'progressive-policy-fee', label: 'Policy fee', amount: 150 }],
      keys: ['progressive:AUTO_PHYSICAL_DAMAGE'],
      subtotal: 4_750,
      pricedAsOneTotal: false,
    },
    isCarrierEstimate: true,
    paymentPlans: [
      { title: 'Pay in full', dueToday: 4750, total: 4750 },
      { title: '4 payments', dueToday: 1440, installments: { count: 3, amount: 1130 }, total: 4830 },
      { title: '9 payments', dueToday: 1065, installments: { count: 8, amount: 475 }, total: 4865 },
      { title: '11 payments', dueToday: 1020, installments: { count: 10, amount: 385 }, total: 4870 },
    ],
    requiredDocuments: [
      { name: 'Loss run reports', isComplete: true },
      { name: 'IFTA return', isComplete: true },
      { name: 'MVRs', awaitingFrom: 'Checkr' },
    ],
    tracksActivation: true,
    requirements: [
      CheckoutRequirement.DOCUMENT_UPLOAD,
      CheckoutRequirement.COI_HOLDERS,
      CheckoutRequirement.TERMS,
      CheckoutRequirement.CARRIER_PAYMENT,
    ],
  },
]

// Off the page while the Corgi and Progressive cards get feedback; each runs its own step order.
export const PARKED_CHECKOUTS: CarrierCheckout[] = [
  {
    quote: {
      id: 'biberk',
      carrierName: 'biBERK',
      iconUrl: `${CARRIER_LOGO_BASE}/biberk-icon.png`,
      iconAlt: 'biBERK',
      coverages: [{ type: WORKERS_COMP, premium: 3_400, limit: 1_000_000, deductible: null }],
      oneTimeFees: [{ id: 'biberk-state-assessment', label: 'State assessment', amount: 68 }],
      keys: ['biberk:WORKERS_COMP'],
      subtotal: 3_468,
      pricedAsOneTotal: false,
    },
    isCarrierEstimate: false,
    acceptsAch: true,
    offerSameCard: true,
    paymentPlans: [
      { title: 'Pay in full', dueToday: 3468, total: 3468 },
      { title: '4 payments', dueToday: 1020, installments: { count: 3, amount: 830 }, total: 3510 },
      { title: '9 payments', dueToday: 748, installments: { count: 8, amount: 345 }, total: 3508 },
      { title: '11 payments', dueToday: 708, installments: { count: 10, amount: 282 }, total: 3528 },
    ],
    requirements: [
      CheckoutRequirement.SIGNATURES,
      CheckoutRequirement.PAYMENT_PLAN,
      CheckoutRequirement.EFFECTIVE_DATE,
      CheckoutRequirement.COI,
      CheckoutRequirement.PAYMENT_INFO,
      CheckoutRequirement.COVERAGE_ACTIVATION,
    ],
  },
]
